import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

// TailAdmin's SidebarContext: expanded (290px) vs collapsed (90px, hover-to-expand) on xl+, and an
// off-canvas drawer below xl. The expanded choice is remembered per browser.
interface SidebarState {
  isExpanded: boolean
  isHovered: boolean
  isMobileOpen: boolean
  setIsHovered: (v: boolean) => void
  toggleSidebar: () => void
  closeMobile: () => void
}

const SidebarContext = createContext<SidebarState | null>(null)
const STORAGE_KEY = 'rabbit.sidebarExpanded'
const XL = 1280

function readExpanded(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) !== 'false' } catch { return true }
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isExpanded, setIsExpanded] = useState(readExpanded)
  const [isHovered, setIsHovered] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(isExpanded)) } catch { /* private mode */ }
  }, [isExpanded])

  // Leaving mobile width with the drawer open would otherwise leave it stuck open over the page.
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= XL) setIsMobileOpen(false) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth >= XL) setIsExpanded(v => !v)
    else setIsMobileOpen(v => !v)
  }, [])
  const closeMobile = useCallback(() => setIsMobileOpen(false), [])

  return (
    <SidebarContext.Provider value={{ isExpanded, isHovered, isMobileOpen, setIsHovered, toggleSidebar, closeMobile }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar(): SidebarState {
  const ctx = useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar must be used inside <SidebarProvider>')
  return ctx
}
